import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { Task, TaskStatus } from '../types/task';
import { format } from 'date-fns';
import { taskService } from '../services/taskService';

interface TaskDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  task: Task | null;
}

const statusColors: Record<TaskStatus, 'default' | 'info' | 'success'> = {
  TODO: 'default',
  IN_PROGRESS: 'info',
  DONE: 'success',
};

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ width: { xs: '100%', sm: '50%' }, mb: 2, pr: { sm: 1 } }}>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{label}</Typography>
    <Typography variant="body1">{value || 'N/A'}</Typography>
  </Box>
);

const MyTaskDetailsModal: React.FC<TaskDetailsModalProps> = ({ open, onClose, onSave, task }) => {
  const [status, setStatus] = useState<TaskStatus | ''>('');

  useEffect(() => {
    if (task) {
      setStatus(task.status);
    }
  }, [task]);

  if (!task) return null;

  const handleStatusChange = (event: SelectChangeEvent<TaskStatus>) => {
    setStatus(event.target.value as TaskStatus);
  };

  const handleSave = async () => {
    if (task && status && status !== task.status) {
      try {
        await taskService.updateTask(task.id, { status });
        onSave();
        onClose();
      } catch (error) {
        console.error('Failed to update task status', error);
        // You might want to show an error to the user here
      }
    }
  };

  const formattedStatus = task.status.replace('_', ' ').toLocaleLowerCase();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {task.title}
          <Chip
            label={formattedStatus}
            color={statusColors[task.status]}
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
          <DetailItem label="Project" value={task.project_details?.name} />
          <DetailItem label="Client" value={task.project_details?.client_details?.name} />
          <DetailItem label="Assigned To" value={`${task.assigned_to_details?.first_name} ${task.assigned_to_details?.last_name}`} />
          <DetailItem label="Due Date" value={task.due_date ? format(new Date(task.due_date), 'MMMM dd, yyyy') : 'N/A'} />
        </Box>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={handleStatusChange}>
            <MenuItem value="TODO">To Do</MenuItem>
            <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
            <MenuItem value="DONE">Done</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {task.description || 'No description provided.'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} disabled={!status || status === task.status} color="primary">
          Save
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MyTaskDetailsModal;